<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { ddlHelper, sessionHelper } from "@/utils";
import { SESSION } from "@/constants";
import { required } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import i18n from '@/i18n'
import { LOOKUP_MODULE } from '@/state/modules/lookup'
import { PRODUCT_PAYMENT_MODULE } from '@/state/modules/productPayment'
import { lookupMethods, productPaymentMethods } from "@/state/helpers";
import Multiselect from "vue-multiselect";
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

/**
 * Tabs & accordions component
 */
export default {
    page: {
        title: i18n.t("label.updateX", [i18n.t("module.productPayment")]),
    },
    components: { Layout, PageHeader, Multiselect, DatePicker },
    beforeRouteLeave(to, from, next) {
        if (to.name != "Product-payment-listing") {
            sessionHelper.clearSearch(SESSION.PRODUCT_PAYMENT_SEARCH);
        }
        next();
    },
    computed: {
        ...mapState(PRODUCT_PAYMENT_MODULE, ["status"]),
        ...mapState(LOOKUP_MODULE),
        data() {
            return this.$store.state.productPayment.details;
        },
        productDdl() {
            return this.$store.state.lookup.productDdl;
        },
    },
    data() {
        return {
            title: i18n.t("label.updateX", [i18n.t("module.productPayment")]),
            options: [
                { value: 0, text: 'Query' },
                { value: 1, text: 'Reply' }
            ],
            items: [
                {
                    text: i18n.t('module.dashboard'),
                    href: "/",
                },
                {
                    text: i18n.t("module.productPayment"),
                    href: "/product-payment",
                },
                {
                    text: i18n.t("label.updateX", [i18n.t('module.productPayment')]),
                    active: true,
                },
            ],
            submitted: false,
            permissions: sessionHelper.getPermissions(),
            updatePermission: null,
            readPermission: null,
            statusDdl: ddlHelper.getActivationStatus(true),
        };
    },
    validations: {
        data: {
            type: { required },
            productID: { required },
            date: { required }
        },
    },
    async created() {
        this.searchCriteria = sessionHelper.retrieveSearch(SESSION.PRODUCT_PAYMENT_SEARCH);
        this.searchCriteriaProductSpecial = sessionHelper.retrieveSearch(SESSION.PRODUCT_SPECIAL_SEARCH);

        if (this.searchCriteriaProductSpecial != null && this.searchCriteriaProductSpecial != '') {
            this.isCloseWhenSuccess = this.searchCriteriaProductSpecial.isCloseWhenSuccess;
        }
        else {
            this.isCloseWhenSuccess = false;
        }

        await this.validPermissions();
        this.lookupProduct({ hasOptionAll: false });
        this.getProductPaymentData();
    },
    methods: {
        ...productPaymentMethods,
        ...lookupMethods,
        async validPermissions() {
            var jsonPermissions = JSON.parse(this.permissions)
            if (jsonPermissions.some(p => p.code === "RND_PPAYMENT_PPU")) {
                this.updatePermission = true;
            }
            else {
                this.updatePermission = false;
            }

            if (jsonPermissions.some(p => p.code === "RND_PPAYMENT_PPR")) {
                this.readPermission = true;
            }
            else {
                this.readPermission = false;
            }
        },
        async getProductPaymentData() {
            this.isLoading = true;
            if (this.readPermission == true) {
                await this.getProductPayment({ id: this.searchCriteria.selectedRecordId });
            }
            this.isLoading = false;
        },

        async requestUpdate() {
            this.submitted = true;
            // stop here if form is invalid
            this.$v.$touch();

            if (this.$v.data.$invalid) {
                console.log("Validation failed");

                for (const field in this.$v.data) {
                    if (this.$v.data[field].$invalid) {
                        console.log(`Validation failed for: ${field}`);
                    }
                }
                return;
            } else {
                let isCloseWhenSuccess = this.isCloseWhenSuccess;

                let data = {
                    id: this.data.uuid,
                    remark: this.data.remark,
                    productID: this.data.productID.value,
                    type: this.data.type.value,
                    date: this.data.date,
                };
                if (this.updatePermission == true) {
                    await this.updateProductPayment({ data, isCloseWhenSuccess });
                }
            }
        },
    },
};
</script>

<template>
    <Layout>
        <PageHeader :title="title" :items="items" />
        <div class="row">
            <div class="col-lg-12">
                <div class="card">
                    <div class="card-body">
                        <form @submit.prevent="requestUpdate" v-if="readPermission == true">
                            <div class="row">
                                <div class="col-md-6">
                                    <b-form-group id="productID" label-cols-sm="4" label-cols-lg="4"
                                        :label="$t('module.product')" label-for="productID">
                                        <multiselect v-model="data.productID" deselect-label="Can't remove this value"
                                            track-by="value" label="text" placeholder="Select one" :options="productDdl"
                                            :searchable="true" :allow-empty="false">
                                        </multiselect>
                                        <div v-if="submitted && !$v.data.productID.required" class="invalid-feedback"
                                            style="display: block;">
                                            <span>{{ $t("message.requiredX", [$t("module.product")]) }}</span>
                                        </div>
                                    </b-form-group>
                                </div>

                                <div class="col-md-6">
                                    <b-form-group id="type" label-cols-sm="4" label-cols-lg="4"
                                        :label="$t('module.type')" label-for="type">
                                        <multiselect v-model="data.type" deselect-label="Can't remove this value"
                                            track-by="value" label="text" placeholder="Select one" :options="options"
                                            :searchable="true" :allow-empty="false">
                                        </multiselect>
                                        <div v-if="submitted && !$v.data.type.required" class="invalid-feedback"
                                            style="display: block;">
                                            <span>{{ $t("message.requiredX", [$t("module.type")]) }}</span>
                                        </div>
                                    </b-form-group>
                                </div>

                                <div class="col-md-6">
                                    <b-form-group id="remark" label-cols-sm="4" label-cols-lg="4"
                                        :label="$t('label.remark')" label-for="remark">
                                        <textarea v-model="data.remark" class="form-control" name="textarea"
                                            rows="5"></textarea>
                                    </b-form-group>
                                </div>

                                <div class="col-md-6">
                                    <b-form-group id="date" label-cols-sm="4" label-cols-lg="4"
                                        :label="$t('label.date')" label-for="date">
                                        <date-picker v-model="data.date" type="date" format="YYYY-MM-DD"
                                            value-type="format" placeholder="Select date"></date-picker>
                                        <div v-if="submitted && !$v.createForm.date.required" class="invalid-feedback"
                                            style="display: block;">
                                            <span>{{ $t("message.requiredX", [$t("label.date")]) }}</span>
                                        </div>
                                    </b-form-group>
                                </div>
                            </div>

                            <div class="mt-4 text-center" style="float: right">
                                <button class="btn btn-primary w-md waves-effect waves-light" style="margin-right: 10px"
                                    type="submit" :disabled="status.create || updatePermission != true">
                                    {{ $t("label.updateX", [$t("module.productPayment")]) }}
                                </button>
                            </div>
                        </form>
                        <div style="text-align: center;" v-else>
                            {{ $t('message.noPermissionMessage') }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>